<template>
  <div>
    <h1>{{ $t("onboard.MFAInput.title") }}</h1>
    <p>{{ $t("onboard.MFAInput.message") }}</p>
    <div class="mfa-input">
      <input
        v-for="(char, index) in code"
        :key="index"
        type="text"
        maxlength="1"
        v-model="code[index]"
        ref="inputs"
        @input="onInput(index)"
        @keydown.backspace="onBackspace(index)"
        @paste="onPaste"
      />
    </div>
    <p class="error-text" v-if="errorMsg">{{ errorMsg }}</p>
    <button
      id="verify-btn"
      @click="verifyMFA"
      :class="{ disabled: !isCodeComplete || errorMsg }"
    >
      {{ $t("onboard.MFAInput.verify") }}
    </button>
    <p class="resend-text">
      {{ $t("onboard.MFAInput.resend-question") }}
      <span
        class="resend-link"
        :class="{ disabled: resendCooldown > 0 }"
        @click="resendCooldown === 0 && resendCode()"
      >
        {{ $t("onboard.MFAInput.resend") }}
        <!-- {{ resendCooldown > 0 ? ` (${resendCooldown}s)` : "" }} -->
      </span>
    </p>
    <SelectLang />
  </div>
</template>

<script>
import SelectLang from "@/components/onboard/SelectLanguage.vue";
import { userService } from "@/services/user.service";

export default {
  name: "MFAInput",
  components: { SelectLang },
  data() {
    return {
      code: ["", "", "", "", "", ""],
      inputs: [],
      resendCooldown: 0,
      expireTime: 30,
      cooldownInterval: null,
      errorMsg: null,
    };
  },
  computed: {
    isCodeComplete() {
      return this.code.join("").length === 6;
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (!localStorage.getItem("mfaExpired")) {
        const exp = Date.now() + this.expireTime * 60 * 1000;
        localStorage.setItem("mfaExpired", exp);
      }
      this.inputs = this.$refs.inputs || [];
      this.focusInput(0);
      this.startCooldown();
    });
  },
  beforeUnmount() {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  },
  watch: {
    code: {
      handler(newCode) {
        this.$emit("update:code", newCode.join(""));
      },
      deep: true,
    },
  },
  methods: {
    onInput(index) {
      this.errorMsg = null;
      let val = this.code[index].toUpperCase().replace(/\s/g, "");
      this.code[index] = val.slice(0, 1);

      if (val && index < this.code.length - 1) {
        this.focusInput(index + 1);
      }
    },
    onBackspace(index) {
      this.errorMsg = null;
      if (!this.code[index] && index > 0) {
        this.focusInput(index - 1);
      }
    },
    onPaste(e) {
      e.preventDefault();
      this.errorMsg = null;
      const pasted = (e.clipboardData || window.clipboardData)
        .getData("text")
        .toUpperCase()
        .replace(/\s/g, "");

      if (/^[A-Z0-9]{6}$/.test(pasted)) {
        this.code = pasted.split("").slice(0, 6);

        this.$nextTick(() => {
          const last = this.$refs.inputs[5];
          if (last) last.focus();
        });
      }
    },
    focusInput(index) {
      if (this.inputs && this.inputs[index]) {
        this.inputs[index].focus();
      }
    },
    resendCode() {
      if (this.isMfaAuthExpired()) return;
      userService.resendMfa().then(() => {});
      this.startCooldown();
    },
    startCooldown() {
      this.resendCooldown = 60;
      if (this.cooldownInterval) clearInterval(this.cooldownInterval);

      this.cooldownInterval = setInterval(() => {
        if (this.resendCooldown > 0) {
          this.resendCooldown--;
        } else {
          clearInterval(this.cooldownInterval);
          this.cooldownInterval = null;
        }
      }, 1000);
    },
    verifyMFA(event) {
      event.preventDefault();
      if (this.errorMsg) return;
      if (this.isMfaAuthExpired()) return;
      const inputCode = this.code.join("");
      userService.verifyMfa({ inputCode }).then((res) => {
        if (res.expired === true) {
          this.errorMsg = this.$t("onboard.MFAInput.mfa-expired");
          return;
        } else if (res.verified === false) {
          this.errorMsg = this.$t("onboard.MFAInput.mfa-incorrect");
          return;
        } else if (res.verified === true) {
          localStorage.removeItem("mfaExpired", true);
          const inviteType = localStorage.getItem("inviteType");
          const pathId = localStorage.getItem("pathId");
          if (inviteType && pathId) {
            // Remove the token from the session.
            localStorage.removeItem(inviteType + "Invite");
            localStorage.removeItem("inviteType");
            localStorage.removeItem("pathId");
            // Move on to profile creation, and pass along a destination.
            this.$router.push(
              "/profile-create?dest=/" + inviteType + "/" + pathId
            );
          } else {
            if (this.$route.query.dest) {
              this.$router.push(this.$route.query.dest);
            } else {
              this.$router.push("/plans");
            }
          }
        }
      });
    },
    isMfaAuthExpired() {
      const mfaExpired = localStorage.getItem("mfaExpired");
      if (mfaExpired) {
        const now = Date.now();
        if (now > parseInt(mfaExpired, 10)) {
          this.$router.replace({
            path: "/login",
            query: { err: "auth_expired" },
          });
          window.location.reload();
          return true;
        }
      }
      return false;
    },
  },
};
</script>

<style scoped type="scss">
.mfa-input {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin: 1rem 0;
}

.mfa-input input {
  width: 3rem;
  height: 3rem;
  text-align: center;
  font-size: 1.5rem;
  border: 2px solid #aaa;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  text-transform: uppercase;
}
.mfa-input input:focus {
  border-color: #1a237e;
  box-shadow: 0 0 5px #1a237e;
}
#verify-btn {
  background-color: #ffccbc;
  width: 100%;
  margin-top: 0;
}
#verify-btn.disabled {
  color: #aaa;
  background-color: #fbe9e7;
  cursor: not-allowed;
  pointer-events: none;
}

.resend-text {
  text-align: center;
  margin-top: 1rem;
  font-size: 0.95rem;
  color: #555;
  margin-bottom: 1rem;
}

.resend-link {
  color: #1a237e;
  cursor: pointer;
  font-weight: 700;
  margin-left: 0.25rem;
  user-select: none;
}

.resend-link.disabled {
  color: #aaa;
  cursor: not-allowed;
  pointer-events: none;
}

.error-text {
  text-align: center;
  color: #ff5151;
  font-weight: 500;
  margin-top: -20px;
}
</style>
